import { useMemo } from "react";
import { useGetList, useTranslate, type Identifier } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { DateField } from "@/components/admin/date-field";
import { Checkbox } from "@/components/ui/checkbox";

import type { CampaignTaskProgress } from "../types";
import { useCampaignTaskStatusLabel } from "./useCampaignStatusChoices";

const statusVariant = (status: string) => {
  if (status === "overdue") return "destructive" as const;
  if (status === "snoozed") return "secondary" as const;
  if (status === "completed") return "outline" as const;
  return "default" as const;
};

const TaskStatusBadge = ({ status }: { status: string }) => {
  const label = useCampaignTaskStatusLabel(status);
  return <Badge variant={statusVariant(status)}>{label}</Badge>;
};

export const CampaignOpenTasksList = ({
  campaignId,
  statusFilter,
  definitionFilter,
  salesFilter,
  showCompleted,
  onToggleShowCompleted,
}: {
  campaignId: Identifier;
  statusFilter?: string | null;
  definitionFilter?: Identifier | null;
  salesFilter?: Identifier | null;
  showCompleted?: boolean;
  onToggleShowCompleted?: (v: boolean) => void;
}) => {
  const translate = useTranslate();
  const { data: rows, isPending } = useGetList<CampaignTaskProgress>(
    "campaign_tasks_progress",
    {
      filter: { campaign_id: campaignId },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "due_date", order: "ASC" },
    },
  );

  const filtered = useMemo(() => {
    let list = rows ?? [];
    if (!showCompleted) {
      list = list.filter((r) => r.status !== "completed");
    }
    if (statusFilter) {
      list = list.filter((r) => r.status === statusFilter);
    }
    if (definitionFilter) {
      list = list.filter(
        (r) => r.campaign_task_definition_id === definitionFilter,
      );
    }
    if (salesFilter) {
      list = list.filter((r) => r.assignee_sales_id === salesFilter);
    }
    return [...list].sort((a, b) => {
      const order = { overdue: 0, open: 1, snoozed: 2, completed: 3 };
      const diff =
        (order[a.status as keyof typeof order] ?? 9) -
        (order[b.status as keyof typeof order] ?? 9);
      if (diff !== 0) return diff;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });
  }, [rows, showCompleted, statusFilter, definitionFilter, salesFilter]);

  if (isPending) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {translate("resources.campaigns.open_tasks.title", { _: "Tasks" })}
        </h3>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={showCompleted}
            onCheckedChange={(v) => onToggleShowCompleted?.(!!v)}
          />
          {translate("resources.campaigns.open_tasks.show_completed", {
            _: "Show completed",
          })}
        </label>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.campaigns.open_tasks.all_completed", {
            _: "All tasks completed.",
          })}
        </p>
      ) : (
        <div className="rounded-md border divide-y">
          {filtered.map((row) => (
            <div
              key={row.task_id}
              className="grid grid-cols-1 md:grid-cols-5 gap-2 p-3 text-sm"
            >
              <div>
                {row.company_id ? (
                  <Link
                    to={`/companies/${row.company_id}/show`}
                    className="font-medium hover:underline"
                  >
                    {row.company_name ??
                      translate("resources.campaigns.open_tasks.company_fallback", {
                        id: row.company_id,
                        _: `Company #${row.company_id}`,
                      })}
                  </Link>
                ) : row.contact_id ? (
                  <Link
                    to={`/contacts/${row.contact_id}/show`}
                    className="font-medium hover:underline"
                  >
                    {row.contact_name?.trim() ||
                      translate("resources.campaigns.open_tasks.contact_fallback", {
                        id: row.contact_id,
                        _: `Contact #${row.contact_id}`,
                      })}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">
                    {translate("resources.campaigns.open_tasks.campaign_task", {
                      _: "Campaign task",
                    })}
                  </span>
                )}
              </div>
              <div className="text-muted-foreground">
                {row.assignee_name ?? "—"}
              </div>
              <div>{row.definition_title}</div>
              <div>
                <DateField source="due_date" record={row} showDate showTime />
              </div>
              <div>
                <TaskStatusBadge status={row.status} />
                {row.completion_note && (
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                    {row.completion_note}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
